import { useTranslation } from "react-i18next";
import React from "react";
import Heading from "../../layout/Heading";
import { useCreateSpecialAnnouncementContext } from "@context/CreateSpecialAnnouncementContext";
import ConfirmationDetail from "@components/shared/ConfirmationDetail";
import ConfirmModal from "@components/shared/ConfirmModal";
import { useCreateSpecialAnnouncement } from "@hooks/mutation/useCreateSpecialAnnouncement";
import { toastError, toastSuccess, toastWarning } from "@utils/toast";
import { isValidationError } from "@utils/formError";
import { DraftNote } from "@components/notes/NoteComposer";
import { flushNoteStepDraft } from "@components/notes/flushNoteStepDraft";
import { mergeDraftNoteIntoRow } from "@components/notes/mergeDraftNoteIntoRow";
import { DBTables } from "@utils/constants/dbTables";

type SpecialAnnouncementLedgerProps = {
  /** Optional draft from the wizard's NoteStep; flushed after the workflow create succeeds. */
  draftNote?: DraftNote;
};

const SpecialAnnouncementLedger: React.FunctionComponent<
  SpecialAnnouncementLedgerProps
> = ({ draftNote }) => {
  const { t } = useTranslation();
  const {
    stepHelpers,
    info,
    navigateForCancel,
    details,
    setWorkflowInstance,
    setWorkflowCompleted,
    setNetworkErrors,
  } = useCreateSpecialAnnouncementContext();
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);

  const { mutateAsync: createSpecialAnnouncementMutation, isLoading } =
    useCreateSpecialAnnouncement();

  const handleOnConfirm = async () => {
    const data = {
      level0ResourceId: details.level0wrs.id,
      accountingPeriodId: details.accountingPeriod?.id,
      startAt: details.startAt ? new Date(details.startAt) : new Date(),
      subscriberIds: details.subscribers.map((i: any) => i.id),
    };
    try {
      let specialAnnouncementIds = [];
      let instance: any;

      const [res, workflowInstance] =
        await createSpecialAnnouncementMutation(data);
      for (const sa of res) {
        specialAnnouncementIds.push(sa.id);
      }

      instance = workflowInstance;

      // FR-1.1 + FR-1 invariant: flush optional NoteStep draft AFTER the entity
      // commits. Special Announcement create produces N per-subscriber records
      // targeting a single Level0 resource (Zone), so the note is anchored to
      // that Zone — which gives the backend a path to roll the note up to the
      // parent Scheme via Level0WRS.parentId (see
      // NoteLucidRepository.resolveDenormalisedReferences). Note failure does
      // NOT roll back the entity; the user gets a warning toast.
      const noteResult = await flushNoteStepDraft(
        draftNote,
        DBTables.Level0WRS,
        details.level0wrs?.id,
      );

      setWorkflowInstance(instance);

      setWorkflowCompleted(true);
      if (noteResult.kind === "failed") {
        toastWarning(
          t("notes.step.flush_failed", {
            artifact: t("common.special_announcements"),
          }),
        );
      } else {
        toastSuccess(t("special_announcement.save_succeed"));
      }
      setNetworkErrors([]);
      stepHelpers.goToNextStep();
    } catch (error: any) {
      const { data } = error?.response;
      const errorMessages = isValidationError(error)
        ? data?.errors?.map((i: any) => i.message)
        : [data?.message];

      toastError(
        <>
          <p>
            {t("special_announcement.save_failed", {
              error: error?.response?.data?.message || error?.message,
            })}
          </p>
          {errorMessages.length && (
            <ul className="list-disc pl-4">
              {errorMessages.map((text: any) => {
                return <li key={text}>{text}</li>;
              })}
            </ul>
          )}
        </>,
      );
      setNetworkErrors(errorMessages);
    }

    setShowConfirmModal(false);
  };

  return (
    <>
      <div className="flex flex-col gap-6 p-6 grow">
        <Heading light={true}>
          {t("special_announcement.confirm_values")}
        </Heading>
        <ConfirmationDetail
          data={[
            info.wrsHierarchies,
            info.subscribers,
            // Note step lives in the Details step (no evidence step in this
            // wizard), so merge the draft into info.details to keep the row's
            // data-index aligned with its step-index for Edit/Previous.
            mergeDraftNoteIntoRow(info.details, draftNote, t, info.details.title),
          ]}
          onEdit={index => stepHelpers.setStep(index)}
        />
      </div>
      <footer className="flex gap-4 p-6  border-t border-gray-200">
        <button
          type="button"
          className="btn-outline-primary"
          onClick={stepHelpers.goToPrevStep}
        >
          {t("common.prev_step")}
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={() => setShowConfirmModal(true)}
        >
          {t("common.ledger")}
        </button>
        <button
          type="button"
          className="btn-outline-primary"
          onClick={navigateForCancel}
        >
          {t("common.cancel")}
        </button>
      </footer>

      <ConfirmModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleOnConfirm}
        isSubmitting={isLoading}
        confirmText={t("common.ledger")}
      >
        {t("special_announcement.modal_confirmation")}
      </ConfirmModal>
    </>
  );
};

export default SpecialAnnouncementLedger;
